import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Stethoscope, HeartPulse, Activity, Calendar, Users, UserPlus, FileText,
  MessageSquare, Building2, Brain, Globe, ClipboardList, ShieldCheck,
  Sparkles, ArrowRight, CheckCircle, Menu, X, Mail, Instagram, Facebook,
  Crown, Loader2, Phone,
} from 'lucide-react';
import { API } from '@/config/api';
import { getErrorMessage } from '@/lib/utils';
import { PLANS, CURRENCIES } from '@/modules/plans/mockData';
import VerticalSwitcher from '@/components/VerticalSwitcher';
import MedicinaHeroSlider from './MedicinaHeroSlider';
import SaludBenefitsSlider from './SaludBenefitsSlider';

// =====================================================================
// Punto Cero Salud — vertical de salud del ecosistema Punto Cero System.
// =====================================================================

const IMG_DOCTOR = 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_CLINIC = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_PATIENT = 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

// Imágenes médicas específicas por herramienta.
const IMG_TOOL_AGENDA = 'https://images.unsplash.com/photo-1641175177307-e419f704a979?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_TOOL_PATIENTS = 'https://images.unsplash.com/photo-1758691462848-ba1e929da259?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_TOOL_DOCS = 'https://images.unsplash.com/photo-1628348070889-cb656235b4eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_TOOL_COMM = 'https://images.unsplash.com/photo-1758691461932-d0aa0ebf6b31?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_TOOL_MGMT = 'https://images.unsplash.com/photo-1758691463384-771db2f192b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';
const IMG_TOOL_AI = 'https://images.unsplash.com/photo-1758691463333-c79215e8bc3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400';

const WA_CO = '573028322083';
const WA_VE = '5804246487378';

const NAV_LINKS = [
  { href: '#nosotros', label: 'Nosotros' },
  { href: '#herramientas', label: 'Herramientas' },
  { href: '#consulta', label: 'Consulta' },
  { href: '#medicos', label: 'Profesionales' },
  { href: '#planes', label: 'Planes' },
];

const MED_COUNTRIES = ['Colombia', 'México', 'Argentina', 'Perú', 'Chile', 'Ecuador', 'Venezuela', 'Bolivia', 'Paraguay', 'Uruguay', 'Guatemala', 'Honduras', 'El Salvador', 'Nicaragua', 'Costa Rica', 'Panamá', 'República Dominicana', 'Brasil'];
const MED_CURRENCIES = MED_COUNTRIES.map((c) => CURRENCIES.find((x) => x.country === c)).filter(Boolean);
const MED_SPECIALTIES = ['Medicina General', 'Odontología', 'Psicología', 'Enfermería', 'Nutrición', 'Fisioterapia y Terapias', 'Pediatría', 'Ginecología', 'Psiquiatría', 'Dermatología', 'Otra'];

const ABOUT = [
  { icon: Users, title: 'Comunidad profesional', desc: 'Una red de profesionales de salud, clínicas y especialistas que crecen y colaboran juntos.', color: '#14b8a6' },
  { icon: Activity, title: 'Tecnología', desc: 'Infraestructura moderna, segura y escalable para tu práctica profesional.', color: '#06b6d4' },
  { icon: ClipboardList, title: 'Organización', desc: 'Centraliza pacientes, agenda y documentos en un solo lugar ordenado.', color: '#3b82f6' },
  { icon: HeartPulse, title: 'Crecimiento profesional', desc: 'Herramientas y presencia digital para expandir tu consulta.', color: '#10b981' },
];

const TOOLS = [
  { icon: Calendar, title: 'Agenda', desc: 'Gestiona citas, turnos y recordatorios de forma inteligente y sin dobles reservas.', color: '#14b8a6', img: IMG_TOOL_AGENDA },
  { icon: UserPlus, title: 'Pacientes', desc: 'Historial clínico, seguimiento y datos de contacto organizados y seguros.', color: '#06b6d4', img: IMG_TOOL_PATIENTS },
  { icon: FileText, title: 'Documentos', desc: 'Recetas, informes y estudios generados y almacenados en un solo lugar.', color: '#3b82f6', img: IMG_TOOL_DOCS },
  { icon: MessageSquare, title: 'Comunicación', desc: 'Mantente en contacto con tus pacientes por canales integrados y directos.', color: '#8b5cf6', img: IMG_TOOL_COMM },
  { icon: Building2, title: 'Gestión profesional', desc: 'Administra tu clínica o consultorio, equipo y operación diaria con claridad.', color: '#10b981', img: IMG_TOOL_MGMT },
  { icon: Brain, title: 'IA futura', desc: 'Asistencia inteligente para notas, resúmenes y apoyo clínico (próximamente).', color: '#f59e0b', img: IMG_TOOL_AI, soon: true },
];

const BENEFITS = [
  { icon: Stethoscope, title: 'Gestión de pacientes', desc: 'Toda la información clínica de cada paciente, ordenada y accesible cuando la necesitas.', color: '#14b8a6' },
  { icon: ClipboardList, title: 'Organización clínica', desc: 'Agenda, documentos y operación de tu consulta bajo un mismo sistema integrado.', color: '#06b6d4' },
  { icon: Globe, title: 'Presencia digital', desc: 'Una identidad profesional online que proyecta confianza y atrae nuevos pacientes.', color: '#10b981' },
];

// Planes: mismos precios (priceUsd) y estructura de Punto Cero, nombres/descr. de salud.
const PU = (slug) => (PLANS.find((p) => p.slug === slug)?.priceUsd || 0);
const MED_PLANS = [
  { id: 'despegue', name: 'El Despegue', processes: '1 médico', color: '#3b82f6', icon: Stethoscope, priceUsd: PU('despegue'), description: 'Para médicos que inician su consulta.', features: ['1 médico', 'Agenda personal', 'Directorio de pacientes', 'Documentos y recetas', 'IA de apoyo (básica)', 'Hasta 50 pacientes activos'] },
  { id: 'salto', name: 'El Salto Estratégico', processes: '1 médico', color: '#f97316', featured: true, icon: Activity, priceUsd: PU('salto-estrategico'), description: 'Para consultas en pleno crecimiento.', features: ['1 médico', 'Agenda avanzada', 'Historia clínica', 'Teleconsulta HD', 'Facturación automática', 'Hasta 150 pacientes'] },
  { id: 'crecimiento', name: 'Consultorio en Crecimiento', processes: 'Hasta 5 médicos', color: '#14b8a6', icon: Building2, priceUsd: PU('firma-crecimiento'), description: 'Para consultorios y clínicas pequeñas.', features: ['Hasta 5 médicos', 'Pacientes ilimitados', 'CRM clínico Pro', 'Multi-agenda', 'IA clínica Pro', 'Teleconsulta grabable', 'Gestión financiera'] },
  { id: 'consolidacion', name: 'Clínica Consolidada', processes: 'Hasta 10 médicos', color: '#10b981', premium: true, icon: Crown, priceUsd: PU('consolidacion-empresarial'), description: 'Para clínicas y redes médicas.', features: ['Hasta 10 médicos', 'Pacientes ilimitados', 'CRM empresarial', 'API e integraciones', 'IA ilimitada', 'SLA garantizado', 'Soporte dedicado'] },
];

function MedicinaHeader() {
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0f172a]/80 border-b border-white/10">
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#14b8a6] to-[#06b6d4] flex items-center justify-center shadow-[0_0_25px_rgba(20,184,166,0.5)]">
            <HeartPulse className="w-6 h-6 text-white" />
          </div>
          <span className="text-white font-bold text-lg leading-tight">
            Punto Cero <span className="bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] bg-clip-text text-transparent">Salud</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="text-white/70 hover:text-white text-sm font-medium transition-colors">{l.label}</a>
          ))}
          <VerticalSwitcher current="medicina" accent="#14b8a6" />
          <Link to="/register?vertical=salud" className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white text-sm font-bold hover:shadow-[0_0_25px_rgba(20,184,166,0.5)] transition-all">
            Crear mi espacio
          </Link>
        </nav>

        <div className="flex items-center gap-2 lg:hidden">
          <VerticalSwitcher current="medicina" accent="#14b8a6" />
          <button className="text-white" onClick={() => setOpen((v) => !v)} aria-label="Menú">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#0f172a]/95 backdrop-blur-md px-6 py-4 space-y-3">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-white/80 hover:text-white py-1">{l.label}</a>
          ))}
          <Link to="/register?vertical=salud" className="block text-center px-5 py-3 rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white font-bold">
            Crear mi espacio profesional
          </Link>
        </div>
      )}
    </header>
  );
}

function SectionTitle({ eyebrow, lead, accent, subtitle }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center max-w-3xl mx-auto mb-14">
      {eyebrow && (
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-[#14b8a6] text-xs font-bold uppercase tracking-[0.25em] mb-5">{eyebrow}</span>
      )}
      <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight tracking-tight">
        {lead} <span className="bg-gradient-to-r from-[#14b8a6] via-[#06b6d4] to-[#10b981] bg-clip-text text-transparent">{accent}</span>
      </h2>
      {subtitle && <p className="text-lg text-white/70 mt-5 leading-relaxed font-light">{subtitle}</p>}
    </motion.div>
  );
}

const inputCls = 'w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#14b8a6] transition-colors';

export default function MedicinaPage() {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currency, setCurrency] = useState(() => MED_CURRENCIES.find((c) => c.country === 'Colombia') || MED_CURRENCIES[0]);

  // Formulario de pacientes (consulta médica) → /public/case-intake
  const [patient, setPatient] = useState({ name: '', area: 'Medicina General', priority: 'media', country: '', city: '', phone: '', email: '', message: '' });
  const [patientStatus, setPatientStatus] = useState({ loading: false, success: false, error: '', message: '', ref: '' });

  // Formulario de médicos (unirse a la red) → /public/lawyer-application
  const [doctor, setDoctor] = useState({ name: '', email: '', phone: '', specialty: 'Medicina General', country: '', city: '', experience: '' });
  const [doctorStatus, setDoctorStatus] = useState({ loading: false, success: false, error: '', message: '' });

  useEffect(() => {
    document.title = 'Punto Cero Salud — Espacio profesional para la salud';
  }, []);

  const handlePatientSubmit = async (e) => {
    e.preventDefault();
    setPatientStatus({ loading: true, success: false, error: '', message: '', ref: '' });
    try {
      const { data } = await axios.post(`${API}/public/case-intake`, {
        name: patient.name,
        description: patient.message,
        legal_area: patient.area,
        priority: patient.priority,
        country: patient.country,
        city: patient.city || null,
        phone: patient.phone || null,
        email: patient.email || null,
      });
      setPatientStatus({ loading: false, success: true, error: '', message: data.message, ref: data.case_number });
      setPatient({ name: '', area: 'Medicina General', priority: 'media', country: '', city: '', phone: '', email: '', message: '' });
    } catch (err) {
      setPatientStatus({ loading: false, success: false, error: getErrorMessage(err, 'Error al enviar. Intente de nuevo.'), message: '', ref: '' });
    }
  };

  const handleDoctorSubmit = async (e) => {
    e.preventDefault();
    setDoctorStatus({ loading: true, success: false, error: '', message: '' });
    try {
      const { data } = await axios.post(`${API}/public/lawyer-application`, {
        full_name: doctor.name,
        email: doctor.email,
        phone: doctor.phone || null,
        specialty: doctor.specialty,
        country: doctor.country,
        city: doctor.city || null,
        experience: doctor.experience,
      });
      setDoctorStatus({ loading: false, success: true, error: '', message: data.message });
      setDoctor({ name: '', email: '', phone: '', specialty: 'Medicina General', country: '', city: '', experience: '' });
    } catch (err) {
      setDoctorStatus({ loading: false, success: false, error: getErrorMessage(err, 'Error al enviar. Intente de nuevo.') });
    }
  };

  const fmtPrice = (n) => new Intl.NumberFormat('es', { style: 'currency', currency: currency.currency_code, maximumFractionDigits: 0 }).format(n);

  return (
    <div id="top" className="min-h-screen bg-[#0f172a] text-white overflow-x-hidden">
      <MedicinaHeader />

      {/* ===== HERO SLIDER (comercial) ===== */}
      <MedicinaHeroSlider />

      {/* ===== QUIÉNES SOMOS ===== */}
      <section id="nosotros" className="relative py-20 px-6">
        <div className="container mx-auto">
          <SectionTitle eyebrow="Quiénes somos" lead="Una comunidad de salud" accent="conectada." subtitle="Punto Cero Salud reúne a médicos, odontólogos, psicólogos, enfermería, nutrición, terapias y clínicas: comunidad, tecnología, organización y crecimiento en una sola plataforma." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ABOUT.map((a, i) => (
              <motion.div key={a.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="group rounded-3xl border border-white/10 backdrop-blur-xl bg-white/[0.04] p-7 hover:border-white/25 hover:bg-white/[0.07] transition-all duration-300">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 border" style={{ background: `${a.color}1f`, borderColor: `${a.color}55` }}>
                  <a.icon className="w-7 h-7" style={{ color: a.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{a.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{a.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SLIDER VERTICAL DE BENEFICIOS (entrada desde arriba) ===== */}
      <SaludBenefitsSlider />

      {/* ===== HERRAMIENTAS (con imágenes reales) ===== */}
      <section id="herramientas" className="relative py-20 px-6">
        <div className="container mx-auto relative z-10">
          <SectionTitle eyebrow="Herramientas" lead="Todo lo que tu práctica" accent="necesita." subtitle="Un ecosistema de herramientas profesionales diseñadas para el día a día médico." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TOOLS.map((t, i) => (
              <motion.div key={t.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }} className="group relative rounded-3xl border border-white/10 bg-white/[0.04] overflow-hidden hover:border-white/25 transition-all duration-300">
                <div className="relative h-40 overflow-hidden">
                  <img src={t.img} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/40 to-transparent" />
                  <div className="absolute bottom-3 left-3 w-11 h-11 rounded-xl flex items-center justify-center border backdrop-blur-md" style={{ background: `${t.color}33`, borderColor: `${t.color}66` }}>
                    <t.icon className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  {t.soon && <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-[#f59e0b]/20 border border-[#f59e0b]/40 text-[#fbbf24] text-[10px] font-bold uppercase tracking-wide">Próximamente</span>}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{t.title}</h3>
                  <p className="text-white/65 text-sm leading-relaxed">{t.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BENEFICIOS ===== */}
      <section id="beneficios" className="relative py-20 px-6">
        <div className="container mx-auto">
          <SectionTitle eyebrow="Beneficios" lead="Más orden, más" accent="tiempo para cuidar." subtitle="Optimiza tu operación y dedica tu energía a lo que realmente importa: tus pacientes." />
          <div className="grid md:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="rounded-3xl border p-8 backdrop-blur-xl" style={{ background: `linear-gradient(135deg, ${b.color}14, rgba(15,23,42,0.5))`, borderColor: `${b.color}44` }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 border" style={{ background: `${b.color}1f`, borderColor: `${b.color}66` }}>
                  <b.icon className="w-8 h-8" style={{ color: b.color }} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{b.title}</h3>
                <p className="text-white/70 leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FORMULARIO PACIENTES (consulta) ===== */}
      <section id="consulta" className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.10]" style={{ backgroundImage: `url('${IMG_PATIENT}')` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-[#0f172a]/85" />
        <div className="container mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-[#14b8a6] text-xs font-bold uppercase tracking-[0.25em] mb-5">Atención al paciente</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">Cuéntenos su <span className="bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] bg-clip-text text-transparent">consulta de salud.</span></h2>
            <p className="text-lg text-white/70 leading-relaxed mb-6 font-light">Describa su caso y le ayudaremos a encontrar al profesional de salud adecuado dentro de nuestra red. Le contactaremos por WhatsApp con el seguimiento.</p>
            <ul className="space-y-3">
              {['Respuesta rápida y confidencial', 'Profesionales verificados', 'Cobertura en 18 países'].map((f) => (
                <li key={f} className="flex items-center gap-2 text-white/80"><CheckCircle className="w-5 h-5 text-[#10b981]" /> {f}</li>
              ))}
            </ul>
          </div>

          <div className="backdrop-blur-xl bg-white/5 border border-white/15 rounded-3xl p-6 md:p-8 shadow-2xl">
            {patientStatus.success ? (
              <div className="text-center py-8" data-testid="patient-success">
                <div className="w-16 h-16 rounded-full bg-[#10b981]/20 border border-[#10b981]/40 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-[#10b981]" /></div>
                <h3 className="text-2xl font-bold text-white mb-2">¡Consulta enviada!</h3>
                <p className="text-white/70">{patientStatus.message || 'Nos pondremos en contacto muy pronto.'}</p>
                {patientStatus.ref && <p className="text-white/50 text-sm mt-2">Referencia: {patientStatus.ref}</p>}
              </div>
            ) : (
              <form onSubmit={handlePatientSubmit} className="space-y-4" data-testid="patient-intake-form">
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">Nombre completo *</label>
                  <input type="text" required value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} className={inputCls} placeholder="Ej. María González" data-testid="patient-name" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Área requerida</label>
                    <select value={patient.area} onChange={(e) => setPatient({ ...patient, area: e.target.value })} className={inputCls} data-testid="patient-area">
                      {MED_SPECIALTIES.map((s) => <option key={s} value={s} className="bg-[#0a0e1a]">{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Prioridad</label>
                    <select value={patient.priority} onChange={(e) => setPatient({ ...patient, priority: e.target.value })} className={inputCls} data-testid="patient-priority">
                      <option value="baja" className="bg-[#0a0e1a]">Baja</option>
                      <option value="media" className="bg-[#0a0e1a]">Media</option>
                      <option value="alta" className="bg-[#0a0e1a]">Alta</option>
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">País *</label>
                    <select required value={patient.country} onChange={(e) => setPatient({ ...patient, country: e.target.value })} className={inputCls} data-testid="patient-country">
                      <option value="" className="bg-[#0a0e1a]">Selecciona…</option>
                      {MED_COUNTRIES.map((c) => <option key={c} value={c} className="bg-[#0a0e1a]">{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Ciudad</label>
                    <input type="text" value={patient.city} onChange={(e) => setPatient({ ...patient, city: e.target.value })} className={inputCls} placeholder="Tu ciudad" data-testid="patient-city" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">WhatsApp / Teléfono</label>
                    <input type="tel" value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value })} className={inputCls} placeholder="Número de contacto" data-testid="patient-phone" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Email</label>
                    <input type="email" value={patient.email} onChange={(e) => setPatient({ ...patient, email: e.target.value })} className={inputCls} placeholder="tu@correo.com" data-testid="patient-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">Describe tu caso *</label>
                  <textarea required rows={3} value={patient.message} onChange={(e) => setPatient({ ...patient, message: e.target.value })} className={inputCls} placeholder="Cuéntanos brevemente qué necesitas…" data-testid="patient-message" />
                </div>
                {patientStatus.error && <p className="text-red-400 text-sm">{patientStatus.error}</p>}
                <button type="submit" disabled={patientStatus.loading} className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="patient-submit">
                  {patientStatus.loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando…</> : <>Enviar consulta <ArrowRight className="w-5 h-5" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ===== FORMULARIO MÉDICOS (unirse a la red) ===== */}
      <section id="medicos" className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e1a] via-[#0f172a] to-[#0a0e1a]" />
        <div className="container mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 backdrop-blur-xl bg-white/5 border border-white/15 rounded-3xl p-6 md:p-8 shadow-2xl">
            {doctorStatus.success ? (
              <div className="text-center py-8" data-testid="doctor-success">
                <div className="w-16 h-16 rounded-full bg-[#10b981]/20 border border-[#10b981]/40 flex items-center justify-center mx-auto mb-4"><CheckCircle className="w-8 h-8 text-[#10b981]" /></div>
                <h3 className="text-2xl font-bold text-white mb-2">¡Solicitud recibida!</h3>
                <p className="text-white/70">{doctorStatus.message || 'Revisaremos tu perfil y te contactaremos pronto.'}</p>
              </div>
            ) : (
              <form onSubmit={handleDoctorSubmit} className="space-y-4" data-testid="doctor-application-form">
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">Nombre completo *</label>
                  <input type="text" required value={doctor.name} onChange={(e) => setDoctor({ ...doctor, name: e.target.value })} className={inputCls} placeholder="Dr. Juan Pérez" data-testid="doctor-name" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Email *</label>
                    <input type="email" required value={doctor.email} onChange={(e) => setDoctor({ ...doctor, email: e.target.value })} className={inputCls} placeholder="medico@ejemplo.com" data-testid="doctor-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Teléfono / WhatsApp</label>
                    <input type="tel" value={doctor.phone} onChange={(e) => setDoctor({ ...doctor, phone: e.target.value })} className={inputCls} placeholder="Número de contacto" data-testid="doctor-phone" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">Especialidad</label>
                    <select value={doctor.specialty} onChange={(e) => setDoctor({ ...doctor, specialty: e.target.value })} className={inputCls} data-testid="doctor-specialty">
                      {MED_SPECIALTIES.map((s) => <option key={s} value={s} className="bg-[#0a0e1a]">{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/80 mb-2">País de ejercicio *</label>
                    <select required value={doctor.country} onChange={(e) => setDoctor({ ...doctor, country: e.target.value })} className={inputCls} data-testid="doctor-country">
                      <option value="" className="bg-[#0a0e1a]">Selecciona…</option>
                      {MED_COUNTRIES.map((c) => <option key={c} value={c} className="bg-[#0a0e1a]">{c}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">Ciudad</label>
                  <input type="text" value={doctor.city} onChange={(e) => setDoctor({ ...doctor, city: e.target.value })} className={inputCls} placeholder="Tu ciudad" data-testid="doctor-city" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">Experiencia / Trayectoria *</label>
                  <textarea required rows={3} value={doctor.experience} onChange={(e) => setDoctor({ ...doctor, experience: e.target.value })} className={inputCls} placeholder="Cuéntanos sobre tu experiencia profesional…" data-testid="doctor-experience" />
                </div>
                {doctorStatus.error && <p className="text-red-400 text-sm">{doctorStatus.error}</p>}
                <button type="submit" disabled={doctorStatus.loading} className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="doctor-submit">
                  {doctorStatus.loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando…</> : <>Unirme a la red <ArrowRight className="w-5 h-5" /></>}
                </button>
              </form>
            )}
          </div>

          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-[#14b8a6] text-xs font-bold uppercase tracking-[0.25em] mb-5">Para profesionales de salud</span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">Haga crecer su práctica y <span className="bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] bg-clip-text text-transparent">ejerza su profesión.</span></h2>
            <p className="text-lg text-white/70 leading-relaxed mb-4 font-light">Únase a nuestra red de profesionales de salud: medicina, odontología, psicología, enfermería, nutrición y terapias. Reciba pacientes, gane presencia profesional y acceda a herramientas diseñadas para su práctica.</p>
            <div className="rounded-2xl overflow-hidden border border-white/10 mt-6">
              <img src={IMG_DOCTOR} alt="Profesional de salud" className="w-full h-56 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECCIÓN CONSULTORIOS ===== */}
      <section id="consultorios" className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.13]" style={{ backgroundImage: `url('${IMG_CLINIC}')` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e1a]/92 via-[#0f172a]/90 to-[#0a0e1a]/92" />
        <div className="container mx-auto relative z-10 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-[#14b8a6] text-xs font-bold uppercase tracking-[0.25em] mb-5">Solución para consultorios</span>
          <h2 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-4">
            El futuro de tu consultorio <span className="bg-gradient-to-r from-[#14b8a6] via-[#06b6d4] to-[#10b981] bg-clip-text text-transparent">comienza aquí.</span>
          </h2>
          <p className="text-lg text-white/70 max-w-3xl mx-auto mb-12 font-light">Transforma la manera en que gestionas tu práctica clínica con una plataforma empresarial diseñada para crecer junto a tu organización.</p>

          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { title: 'Consultorio en crecimiento', sub: 'Hasta 5 médicos', color: '#14b8a6', icon: Sparkles },
              { title: 'Consultorio consolidado', sub: 'Más de 10 médicos', color: '#06b6d4', icon: Crown },
            ].map((opt) => (
              <motion.div key={opt.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative backdrop-blur-2xl rounded-3xl p-8 text-center" style={{ background: `linear-gradient(135deg, ${opt.color}1f, rgba(15,23,42,0.5))`, border: `1px solid ${opt.color}55` }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto border" style={{ background: `${opt.color}1f`, borderColor: `${opt.color}66` }}>
                  <opt.icon className="w-8 h-8" style={{ color: opt.color }} />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{opt.title}</h3>
                <p className="text-lg font-semibold" style={{ color: opt.color }}>{opt.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PLANES (4 planes + toggle + selector de moneda) ===== */}
      <section id="planes" className="relative py-20 px-6">
        <div className="container mx-auto">
          <SectionTitle eyebrow="Planes" lead="Planes para profesionales" accent="y clínicas." subtitle="Los mismos planes de Punto Cero, adaptados a tu práctica profesional de salud. Precios en la moneda de tu país." />

          {/* Controles: toggle + selector de moneda */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-12">
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10">
              <button onClick={() => setBillingCycle('monthly')} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${billingCycle === 'monthly' ? 'bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white shadow-[0_4px_20px_rgba(20,184,166,0.4)]' : 'text-white/60 hover:text-white'}`} data-testid="toggle-monthly">Mensual</button>
              <button onClick={() => setBillingCycle('annual')} className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${billingCycle === 'annual' ? 'bg-gradient-to-r from-[#10b981] to-[#059669] text-white shadow-[0_4px_20px_rgba(16,185,129,0.4)]' : 'text-white/60 hover:text-white'}`} data-testid="toggle-annual">Anual · 1 mes gratis</button>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#14b8a6]" />
              <select
                value={currency.country}
                onChange={(e) => setCurrency(MED_CURRENCIES.find((c) => c.country === e.target.value))}
                className="px-4 py-2.5 rounded-full bg-white/5 border border-white/15 text-white text-sm font-semibold focus:outline-none focus:border-[#14b8a6]"
                data-testid="currency-selector"
              >
                {MED_CURRENCIES.map((c) => <option key={c.country} value={c.country} className="bg-[#0a0e1a]">{c.country} — {c.currency_code}</option>)}
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {MED_PLANS.map((plan, i) => {
              const priceMonthly = Math.round(plan.priceUsd * currency.exchange_rate);
              const annualPrice = priceMonthly * 11; // 12 meses - 1 mes gratis
              const displayPrice = billingCycle === 'annual' ? annualPrice : priceMonthly;
              const monthlyEq = billingCycle === 'annual' ? Math.round(annualPrice / 12) : priceMonthly;
              return (
                <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className={`relative backdrop-blur-xl rounded-3xl p-6 border transition-all duration-300 hover:scale-[1.02] flex flex-col ${plan.premium ? 'bg-gradient-to-br from-[#10b981]/15 via-[#0f172a]/50 to-[#14b8a6]/10 border-[#10b981]/50 lg:-translate-y-2' : plan.featured ? 'bg-gradient-to-br from-[#14b8a6]/10 to-[#06b6d4]/5 border-[#14b8a6]/40' : 'bg-white/5 border-white/10'}`}
                  data-testid={`plan-${plan.id}`}>
                  {plan.premium && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#10b981] via-[#14b8a6] to-[#10b981] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1 whitespace-nowrap"><Crown className="w-3 h-3" /> Premium</div>}
                  {plan.featured && !plan.premium && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">Más Popular</div>}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${plan.color}20`, borderColor: `${plan.color}40`, borderWidth: 1 }}>
                      <plan.icon className="w-5 h-5" style={{ color: plan.color }} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: plan.color }}>{plan.name}</div>
                      <div className="text-xs text-white/40">{plan.processes}</div>
                    </div>
                  </div>
                  <div className="mb-1">
                    <div className="flex items-baseline gap-1"><span className="text-3xl font-bold text-white">{fmtPrice(displayPrice)}</span></div>
                    {billingCycle === 'annual' ? <div className="text-xs text-[#10b981] mt-1">≈ {fmtPrice(monthlyEq)}/mes · 1 mes gratis</div> : <div className="text-xs text-white/40 mt-1">por mes</div>}
                  </div>
                  <p className="text-xs text-white/60 mt-2 mb-5">{plan.description}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {plan.features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-xs text-white/80"><CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: plan.color }} /><span>{f}</span></li>
                    ))}
                  </ul>
                  <Link to={`/register?vertical=salud&plan=${plan.id}&cycle=${billingCycle}`}
                    className={`block w-full py-3 rounded-xl font-bold text-center text-sm transition-all ${plan.premium ? 'bg-gradient-to-r from-[#10b981] via-[#14b8a6] to-[#10b981] text-white hover:shadow-[0_10px_30px_rgba(16,185,129,0.5)]' : plan.featured ? 'bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white hover:shadow-[0_10px_30px_rgba(20,184,166,0.4)]' : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'}`}
                    data-testid={`plan-cta-${plan.id}`}>
                    Comenzar con {plan.name}
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <p className="text-white/50 text-sm mb-4">¿Dudas sobre qué plan elegir? Escríbenos y te asesoramos.</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href={`https://wa.me/${WA_CO}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#25D366] text-white font-bold hover:shadow-[0_0_25px_rgba(37,211,102,0.5)] transition-all" data-testid="plans-whatsapp">
                <Phone className="w-5 h-5" /> Hablar por WhatsApp
              </a>
              <a href="#consulta" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/5 transition-all">
                <MessageSquare className="w-5 h-5 text-[#14b8a6]" /> Asistente / Soporte
              </a>
            </div>
            <p className="text-white/40 text-xs mt-6">Precios de referencia calculados desde USD a {currency.currency_name} ({currency.currency_code}). Puedes cambiar o cancelar tu plan cuando quieras.</p>
          </div>
        </div>
      </section>

      {/* ===== COMUNIDAD ===== */}
      <section id="comunidad" className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e1a] via-[#0f172a] to-[#0a0e1a]" />
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.12]" style={{ backgroundImage: `url('${IMG_PATIENT}')` }} aria-hidden="true" />
        <div className="absolute inset-0 bg-[#0f172a]/70" />
        <div className="container mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="max-w-4xl mx-auto text-center rounded-3xl border border-white/10 backdrop-blur-xl bg-white/[0.04] p-10 md:p-14">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#14b8a6] to-[#06b6d4] flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(20,184,166,0.5)]">
              <Globe className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">Forma parte de una <span className="bg-gradient-to-r from-[#14b8a6] via-[#06b6d4] to-[#10b981] bg-clip-text text-transparent">red profesional internacional.</span></h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8 font-light">Conecta con médicos, clínicas y especialistas de toda la región dentro del ecosistema Punto Cero System.</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {['Médicos', 'Clínicas', 'Especialistas', 'Consultorios'].map((c) => (
                <span key={c} className="px-4 py-2 rounded-full bg-white/[0.06] border border-white/12 text-sm font-semibold text-white/85">{c}</span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#14b8a6]/15 via-[#0f172a] to-[#06b6d4]/15" />
        <div className="container mx-auto relative z-10 text-center max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">Tu espacio profesional médico <span className="bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] bg-clip-text text-transparent">comienza hoy.</span></h2>
            <p className="text-lg text-white/70 mb-9 font-light">Crea tu cuenta y empieza a organizar tu práctica en minutos.</p>
            <Link to="/register?vertical=salud" data-testid="cta-final-medicina" className="relative inline-flex items-center px-10 py-5 rounded-2xl bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] text-white font-bold text-lg overflow-hidden group shadow-[0_0_40px_rgba(20,184,166,0.5)]">
              <span className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-[#14b8a6] to-[#06b6d4] opacity-50 blur-md animate-pulse pointer-events-none" />
              <span className="absolute top-0 left-[-60%] h-full w-1/3 bg-white/40 skew-x-[-20deg] blur-md group-hover:left-[140%] transition-all duration-700 ease-out pointer-events-none" />
              <span className="relative flex items-center"><Sparkles className="w-5 h-5 mr-2" /> Crear mi espacio profesional <ArrowRight className="w-5 h-5 ml-2" /></span>
            </Link>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-7 text-sm text-white/70">
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#10b981]" /> Registro inmediato</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#10b981]" /> Datos protegidos</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#10b981]" /> Sin instalaciones</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="relative border-t border-white/10 bg-[#0b1220] px-6 py-12">
        <div className="container mx-auto grid md:grid-cols-3 gap-8 items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#14b8a6] to-[#06b6d4] flex items-center justify-center"><HeartPulse className="w-5 h-5 text-white" /></div>
            <span className="text-white font-bold">Punto Cero Medicina</span>
          </div>
          <p className="text-white/50 text-sm text-center">Parte del ecosistema <span className="text-white/80 font-semibold">Punto Cero System</span>.</p>
          <div className="flex items-center justify-center md:justify-end gap-4">
            <a href="mailto:puntocerolegal@gmail.com" className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:border-[#14b8a6] transition-all" aria-label="Correo"><Mail className="w-5 h-5" /></a>
            <a href="#top" className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:border-[#14b8a6] transition-all" aria-label="Instagram"><Instagram className="w-5 h-5" /></a>
            <a href="#top" className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:border-[#14b8a6] transition-all" aria-label="Facebook"><Facebook className="w-5 h-5" /></a>
          </div>
        </div>
        <div className="container mx-auto mt-8 pt-6 border-t border-white/5 text-center text-white/40 text-xs">© {new Date().getFullYear()} Punto Cero System · Punto Cero Medicina</div>
      </footer>

      {/* ===== WHATSAPP FLOTANTE (soporte) ===== */}
      <a
        href={`https://wa.me/${WA_CO}`}
        target="_blank"
        rel="noreferrer"
        aria-label="Soporte WhatsApp"
        title="Soporte WhatsApp"
        data-testid="floating-whatsapp"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-[0_10px_30px_rgba(37,211,102,0.5)] hover:scale-110 transition-transform"
      >
        <Phone className="w-7 h-7 text-white" />
      </a>
    </div>
  );
}
